// User profile management section - allows users to edit their profile
import React, { useState, useEffect } from 'react';
import { useRestAuth } from '../providers/RestAuthProvider';
import { useLocalization } from '../providers/LocalizationProvider';
import { useToast } from '../providers/ToastProvider';
import { ApiError } from '../lib/errors/ApiError';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { logger } from '../lib/utils/logger';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/DropdownMenu';

// Tabler flag classes for languages
const LANGUAGE_OPTIONS = [
  { code: 'fr', name: 'Français', flag: 'flag flag-xs flag-country-fr' },
  { code: 'en', name: 'English', flag: 'flag flag-xs flag-country-us' },
  { code: 'es', name: 'Español', flag: 'flag flag-xs flag-country-es' },
  { code: 'pt', name: 'Português', flag: 'flag flag-xs flag-country-pt' }
];

export default function ProfileSection({ dataSource }) {
  // Get hooks for authentication, notifications and localization
  const { user, refreshUser } = useRestAuth();
  const { t } = useLocalization();
  const { notify } = useToast();
  
  // Local state for form management
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    commonLanguage: '',
    culturalLanguage: ''
  });

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || user.name || '',
        password: '',
        confirmPassword: '',
        commonLanguage: user.commonLanguage || 'fr',
        culturalLanguage: user.culturalLanguage || ''
      });
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    // Validate form
    if (formData.password && formData.password !== formData.confirmPassword) {
      notify({ type: 'error', message: t('profile.passwordMismatch') });
      return;
    }

    if (formData.password && formData.password.length < 6) {
      notify({ type: 'error', message: t('profile.passwordTooShort') });
      return;
    }

    setIsLoading(true);
    try {
      await dataSource.api.updateUser(user.id, {
        firstName: formData.firstName,
        name: formData.lastName,
        commonLanguage: formData.commonLanguage,
        culturalLanguage: formData.culturalLanguage,
        password: formData.password
      });

      await refreshUser();
      notify({ type: 'success', message: t('profile.profileUpdated') });

      setIsEditing(false);
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));
      
      logger.info('Profile updated successfully');
    } catch (error) {
      logger.error('Failed to update profile', error);
      notify({ type: 'error', message: error instanceof ApiError ? error.getUserMessage() : error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original user data
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || user.name || '',
        password: '',
        confirmPassword: '',
        commonLanguage: user.commonLanguage || 'fr',
        culturalLanguage: user.culturalLanguage || ''
      });
    }
    setIsEditing(false);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('profile.title')}</h1>
      </div>

      <Card className="p-6">
        <div className="space-y-6">
          {/* Read-only fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {t('users.email')}
              </label>
              <Input
                value={user.email}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                {t('profile.emailCannotBeChanged')}
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {t('users.role')}
              </label>
              <Input
                value={t(`roles.${user.role}`)}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                {t('profile.roleCannotBeChanged')}
              </p>
            </div>
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('users.firstName')} *
              </label>
              <Input
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                disabled={!isEditing}
                placeholder={t('users.firstName')}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('common.name')} *
              </label>
              <Input
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                disabled={!isEditing}
                placeholder={t('common.name')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('users.commonLanguage')}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between"
                    disabled={!isEditing}
                  >
                    <div className="flex items-center gap-2">
                      <span className={LANGUAGE_OPTIONS.find(lang => lang.code === formData.commonLanguage)?.flag}></span>
                      <span>{LANGUAGE_OPTIONS.find(lang => lang.code === formData.commonLanguage)?.name}</span>
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-full">
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => handleInputChange('commonLanguage', lang.code)}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <span className={lang.flag}></span>
                      <span className="flex-1 text-sm font-medium">
                        {lang.name}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('users.culturalLanguage')}
              </label>
              <Input
                value={formData.culturalLanguage}
                onChange={(e) => handleInputChange('culturalLanguage', e.target.value)}
                disabled={!isEditing}
                placeholder={t('users.culturalLanguage')}
              />
            </div>
          </div>

          {/* Password fields */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t('profile.changePassword')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('profile.newPassword')}
                </label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  disabled={!isEditing}
                  placeholder={t('profile.newPassword')}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('profile.confirmPassword')}
                </label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  disabled={!isEditing}
                  placeholder={t('profile.confirmPassword')}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('profile.passwordHint')}
            </p>
          </div>

          {/* Action buttons inside Card */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            {!isEditing ? (
              <Button onClick={() => setIsEditing(true)}>
                {t('common.edit')}
              </Button>
            ) : (
              <>
                <Button 
                  onClick={handleSave} 
                  disabled={isLoading}
                  className="min-w-[100px]"
                >
                  {isLoading ? t('common.loading') : t('common.save')}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  {t('common.cancel')}
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
